import { BaseService } from "../../core/baseService";
import {
  MenuItemServiceInterface,
  CreateMenuItemParams,
  UpdateMenuItemParams,
  DeleteMenuItemParams,
  ToggleSoldOutParams,
  UploadMenuItemImageParams,
  BatchUpdateMenuItemsParams,
} from "../types/menu.types";
import { MenuItemAdmin } from "../../../models/MenuItemAdmin";
import { ApiResult } from "../../core/types";

export class MenuItemService
  extends BaseService
  implements MenuItemServiceInterface
{
  async getItems(): Promise<ApiResult<MenuItemAdmin[]>> {
    return this.handleRequest<MenuItemAdmin[]>(
      this.client.get("/api/admin/menu/items"),
    );
  }

  async createItem(
    params: CreateMenuItemParams,
  ): Promise<ApiResult<MenuItemAdmin>> {
    const { tags, allergens, translations, ...restData } = params;

    const createData = {
      ...restData,
      tags: tags ? tags.join(",") : undefined,
      allergens: allergens || undefined,
      translations: translations || [],
    };

    return this.handleRequest<MenuItemAdmin>(
      this.client.post("/api/admin/menu/items", createData),
    );
  }

  async updateItem(
    params: UpdateMenuItemParams,
  ): Promise<ApiResult<MenuItemAdmin>> {
    const { itemId, tags, allergens, translations, ...restData } = params;

    const updateData = {
      ...restData,
      tags: tags ? tags.join(",") : undefined,
      allergens: allergens || undefined,
      translations: translations || [],
    };

    return this.handleRequest<MenuItemAdmin>(
      this.client.put(`/api/admin/menu/items/${itemId}`, updateData),
    );
  }

  async deleteItem(params: DeleteMenuItemParams): Promise<ApiResult<void>> {
    return this.handleRequest<void>(
      this.client.delete(`/api/admin/menu/items/${params.itemId}`),
    );
  }

  async toggleSoldOut(
    params: ToggleSoldOutParams,
  ): Promise<ApiResult<void>> {
    return this.handleRequest<void>(
      this.client.patch(`/api/admin/menu/items/${params.itemId}/sold-out`, {
        isSoldOut: params.isSoldOut,
      }),
    );
  }

  async uploadItemImage(
    params: UploadMenuItemImageParams,
  ): Promise<ApiResult<{ imageUrl: string }>> {
    const formData = new FormData();
    formData.append("file", params.image); // Backend expects 'file', not 'image'

    // For FormData, we need to remove the Content-Type header completely
    // so the browser can set it with the proper boundary
    return this.handleRequest<{ imageUrl: string }>(
      this.client.post(
        `/api/admin/menu/items/${params.itemId}/image`,
        formData,
        {
          transformRequest: [
            (data, headers) => {
              // Remove Content-Type header for FormData
              delete headers["Content-Type"];
              return data;
            },
          ],
        },
      ),
    );
  }

  async batchUpdateItemsOrder(
    params: BatchUpdateMenuItemsParams,
  ): Promise<ApiResult<void>> {
    return this.handleRequest<void>(
      this.client.put(
        "/api/admin/menu/items/batch-update-order-category",
        params.updates,
      ),
    );
  }
}
